import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    setCart([...cart, product]);
    toast.success(`${product.name} adicionado ao carrinho!`);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
    toast.info('Item removido do carrinho');
  };

  const getTotalPrice = () => {
    return cart.reduce((sum, item) => sum + item.price, 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Faça login para finalizar a compra');
      navigate('/login');
      return;
    }

    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    try {
      const orderData = {
        products: cart.map(item => ({ id: item.id, name: item.name, price: item.price })),
        total: parseFloat(getTotalPrice())
      };
      
      await axios.post(`${API}/orders`, orderData);
      toast.success('Pedido realizado com sucesso!');
      setCart([]);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao realizar pedido');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4" data-testid="shop-title">Loja Oficial</h1>
          <p className="text-xl">Produtos e acessórios oficiais do Pequeninos do Jockey</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Products */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="animate-spin h-12 w-12 text-red-600" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {products.map((product) => (
                  <Card key={product.id} className="hover:shadow-xl transition" data-testid={`product-card-${product.id}`}>
                    <CardContent className="p-0">
                      <div className="aspect-square overflow-hidden">
                        <img 
                          src={product.photo_url} 
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-110 transition duration-300"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1587328943103-43b812d0b7f1?w=500';
                          }}
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                        <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-red-600">
                            R$ {product.price.toFixed(2)}
                          </span>
                          <Button 
                            onClick={() => addToCart(product)} 
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <ShoppingCart className="mr-2" size={18} />
                            Comprar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Cart Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <ShoppingCart className="mr-2 text-red-600" />
                  Carrinho
                </h2>
                
                {cart.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Carrinho vazio</p>
                ) : (
                  <>
                    <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                      {cart.map((item, index) => (
                        <div key={index} className="flex justify-between items-center border-b pb-3">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{item.name}</p>
                            <p className="text-red-600 font-bold">R$ {item.price.toFixed(2)}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => removeFromCart(index)}
                          >
                            Remover
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="border-t pt-4 mb-4">
                      <div className="flex justify-between items-center text-xl font-bold">
                        <span>Total:</span>
                        <span className="text-red-600">R$ {getTotalPrice()}</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleCheckout} 
                      className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
                      data-testid="checkout-button"
                    >
                      Finalizar Compra
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Shop;